/**
 * Trimiterea directa catre imprimanta Datecs DPP-450, fara driver: pe USB
 * prin WebUSB, pe Bluetooth prin Web Serial.
 *
 * USB: pe macOS imprimanta apare ca dispozitiv din clasa Printer fara driver,
 * deci Chrome o poate revendica. Pe Windows interfata e luata de driverul de
 * imprimanta al sistemului si revendicarea esueaza; acolo se printeaza prin
 * driverul Datecs, cu dialogul obisnuit de print.
 *
 * Bluetooth: Chrome vede doar imprimantele deja imperecheate cu sistemul. Pe
 * Android merge; pe macOS 26 Chrome nu primeste raspunsul la cautarea de
 * servicii a imprimantei (SDP timeout), deci pe Mac ramane USB-ul.
 */

export type Connection = 'usb' | 'bluetooth';

/** Trimite o lucrare pe conexiunea aleasa deja. */
export type PrinterSender = (job: Uint8Array) => Promise<void>;

const DPP_450: USBDeviceFilter = { vendorId: 0xfff0, productId: 0x00d2 };

const PRINTER_CLASS = 7;

/** Serial Port Profile, serviciul Bluetooth clasic pe care asculta imprimanta. */
const SERIAL_PORT_PROFILE = '00001101-0000-1000-8000-00805f9b34fb';

const CHUNK_SIZE = 4096;

/** Conexiunile pe care browserul curent le poate folosi. */
export function supportedConnections(): Connection[] {
    if (typeof navigator === 'undefined') return [];

    const connections: Connection[] = [];
    if ('usb' in navigator) connections.push('usb');
    if ('serial' in navigator) connections.push('bluetooth');
    return connections;
}

/**
 * Imprimanta aleasa anterior sau, la prima folosire, lista din Chrome. Lista
 * merge doar imediat dupa un click, deci se apeleaza inainte de orice lucru
 * lung; trimiterea propriu-zisa vine dupa, prin functia intoarsa.
 */
export async function connectPrinter(
    connection: Connection,
): Promise<PrinterSender> {
    if (connection === 'bluetooth') {
        const port = await findBluetoothPrinter();
        return (job) => sendOverBluetooth(port, job);
    }

    const device = await findUsbPrinter();
    return (job) => sendOverUsb(device, job);
}

async function findUsbPrinter(): Promise<USBDevice> {
    const known = await navigator.usb.getDevices();
    const printer = known.find(
        (device) =>
            device.vendorId === DPP_450.vendorId &&
            device.productId === DPP_450.productId,
    );

    return printer || navigator.usb.requestDevice({ filters: [DPP_450] });
}

async function findBluetoothPrinter(): Promise<SerialPort> {
    const known = await navigator.serial.getPorts();
    const printer = known.find(
        (port) =>
            port.getInfo().bluetoothServiceClassId === SERIAL_PORT_PROFILE,
    );

    return (
        printer ||
        navigator.serial.requestPort({
            filters: [{ bluetoothServiceClassId: SERIAL_PORT_PROFILE }],
            allowedBluetoothServiceClassIds: [SERIAL_PORT_PROFILE],
        })
    );
}

/** Interfata de printare si endpoint-ul pe care pleaca datele. */
function usbPrinterChannel(device: USBDevice) {
    const config = device.configuration;
    const printer =
        config &&
        config.interfaces.find(
            (item) => item.alternate.interfaceClass === PRINTER_CLASS,
        );
    const endpoint =
        printer &&
        printer.alternate.endpoints.find(
            (item) => item.direction === 'out' && item.type === 'bulk',
        );

    if (!printer || !endpoint) {
        throw new Error('Dispozitivul ales nu are o interfata USB de printare.');
    }

    return {
        interfaceNumber: printer.interfaceNumber,
        endpointNumber: endpoint.endpointNumber,
    };
}

/*
 * Ambele conexiuni se deschid doar pe durata lucrarii: o imprimanta oprita
 * sau scoasa intre doua printari nu lasa in urma o sesiune moarta.
 */

async function sendOverUsb(device: USBDevice, job: Uint8Array): Promise<void> {
    await device.open();

    try {
        if (!device.configuration) await device.selectConfiguration(1);

        const channel = usbPrinterChannel(device);
        await device.claimInterface(channel.interfaceNumber);

        try {
            for (let start = 0; start < job.length; start += CHUNK_SIZE) {
                const result = await device.transferOut(
                    channel.endpointNumber,
                    job.subarray(start, start + CHUNK_SIZE),
                );

                if (result.status !== 'ok') {
                    throw new Error(`Transferul USB s-a oprit (${result.status}).`);
                }
            }
        } finally {
            await device.releaseInterface(channel.interfaceNumber);
        }
    } finally {
        await device.close();
    }
}

async function sendOverBluetooth(
    port: SerialPort,
    job: Uint8Array,
): Promise<void> {
    // Pe Bluetooth viteza portului nu conteaza, dar Web Serial o cere.
    await port.open({ baudRate: 115200 });

    try {
        if (!port.writable) {
            throw new Error('Imprimanta nu accepta date pe Bluetooth.');
        }

        const writer = port.writable.getWriter();
        try {
            for (let start = 0; start < job.length; start += CHUNK_SIZE) {
                await writer.write(job.subarray(start, start + CHUNK_SIZE));
            }
            // Asteapta sa plece tot ce e in buffer inainte de inchidere.
            await writer.close();
        } finally {
            writer.releaseLock();
        }
    } finally {
        await port.close();
    }
}

/** Mesajul pentru utilizator, dupa tipul erorii si conexiune. */
export function directPrintErrorMessage(
    error: any,
    connection: Connection,
): string {
    const name = error && error.name;

    if (name === 'NotFoundError') {
        return connection === 'bluetooth'
            ? 'Nu a fost aleasa nicio imprimanta. Daca nu apare in lista, ' +
                  'imperecheaz-o intai din setarile Bluetooth ale telefonului.'
            : 'Nu a fost aleasa nicio imprimanta.';
    }

    if (name === 'SecurityError' || name === 'NetworkError') {
        return connection === 'bluetooth'
            ? 'Imprimanta nu raspunde pe Bluetooth: verifica sa fie pornita. ' +
                  'Daca tot nu merge, sterge-o din lista Bluetooth a ' +
                  'telefonului si imperecheaz-o din nou.'
            : 'Imprimanta nu poate fi folosita direct: verifica cablul USB ' +
                  'si sa nu fie deschisa in alt program. Pe Windows foloseste ' +
                  'butonul Printeaza, cu driverul Datecs.';
    }

    return (error && error.message) || String(error);
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--2!./src/utils/direct-printer.ts